import { Component, effect, inject, OnDestroy } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormNumberComponent, FormSelectComponent } from '@shared/forms';
import { LessonFacade } from '@app/store/lesson';
import { LessonType } from '@app/store/shared/models';
import { ItemSelectionService } from '../item-selection.service';

@Component({
    selector: 'app-create-lesson',
    templateUrl: './create-lesson.component.html',
    styleUrls: ['./create-lesson.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, RouterLink, ButtonModule, FormInputComponent, FormNumberComponent, FormSelectComponent],
})
export class CreateLessonComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly lessonFacade = inject(LessonFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly translateService = inject(TranslateService);
    private readonly itemSelectionService = inject(ItemSelectionService);

    readonly saving = this.lessonFacade.saving;

    readonly typeOptions = getTypeOptions(this.translateService);

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        duration: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
        type: this.formBuilder.control<LessonType>('video', [FormValidators.Required]),
    });

    constructor() {
        effect(() => {
            const lesson = this.lessonFacade.lesson();
            if (this.lessonFacade.saved() && lesson) {
                this.itemSelectionService.select({ id: String(lesson.id), type: 'lesson' });
                this.close();
            }
        });

        effect(() => {
            if (this.lessonFacade.saving() === false && this.lessonFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.lessonFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.form.disable();
            const value = this.form.getRawValue();
            this.lessonFacade.createLesson({
                name: value.name,
                duration: value.duration ?? 0,
                type: value.type,
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { dialog: null } }]);
    }
}

function getTypeOptions(translateService: TranslateService): { label: string; value: LessonType }[] {
    return [
        { value: 'video', label: translateService.instant('lessons.type.video') },
        { value: 'audio', label: translateService.instant('lessons.type.audio') },
        { value: 'text', label: translateService.instant('lessons.type.text') },
    ];
}
